import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Send the visitor home when they land on a page whose feature flag is OFF.
 *
 * Deliberately an EFFECT rather than a render-time `<Navigate>`, for two reasons that only
 * appeared once platform flags started being seeded into the SSG snapshot
 * (scripts/prefetch-public-data.mjs):
 *
 *  1. During the PRERENDER a render-time redirect would fire immediately and bake an empty
 *     redirect stub into the static HTML — the exact failure mode that kept those flags out of
 *     the snapshot in the first place, and the thing that made the CLS fix look unsafe.
 *  2. On the CLIENT it would fire on the FIRST render, which must match the server markup
 *     byte-for-byte. Redirecting there disagrees with the prerendered HTML and trips a
 *     hydration mismatch (React #418) — the same class of bug the nav self-link filter in
 *     PublicHeader already guards against.
 *
 * Running after mount keeps the first client render identical to the prerendered markup, so the
 * page hydrates cleanly and only then navigates away.
 *
 * This is a UX nicety, not the security boundary. A direct request to a disabled page never
 * reaches React at all: nginx's `auth_request` → /api/page-gate returns 404 with
 * `X-Robots-Tag: noindex, nofollow`. This only covers client-side navigation from another page,
 * where no server request happens.
 *
 * Pass `disabled` already resolved (i.e. `!isLoading && !feature(kind)`), so a page never
 * redirects on the optimistic-loading pass.
 */
export function useDisabledPageRedirect(disabled: boolean, to = '/'): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (disabled) navigate(to, { replace: true });
  }, [disabled, to, navigate]);
}
